<template>
  <v-row id="termLifeCardPaymentAlert" class="px-3">
    <!-- ! This is the pay now blue/green bar banner -->
    <OnexAlert
      v-if="!showPayInProgress && showPayNowLinkThisCard && !isAccessNone"
      :type="Notifications[paynowType]"
      icon
    >
      <template #default>
        <span v-html="payNowLinkDesc"></span>
      </template>
      <template #description>
        <a class="font-weight-bold" @click="openDocumentPDF">{{
          $t('L_view_premium_due_notice')
        }}</a>
      </template>
      <template #cta>
        <OnexButton @click="payNow" large color="primary" :prepend-icon="mdiCurrencyUsd">
          {{ $t('L_pay_now') }}
        </OnexButton>
      </template>
    </OnexAlert>
    <!-- ! Pay in progress -->
    <v-col
      v-else-if="!isPending && !isAccessNone"
      cols="12"
      class="background_color_light_blue px-4"
      :class="$vuetify.display.smAndDown ? 'font-size--14' : 'font-size--16'"
    >
      <span class="font-weight-regular" id="billing-mode">
        {{ one_of_billing_mode[0] }}
      </span>
      <span class="font-weight-bold pl-2 pr-2">
        {{ one_of_billing_mode[1] }}
      </span>
      <span class="pl-2 pr-2" v-if="$vuetify.display.smAndUp">
        <span v-if="one_of_billing_mode[3] !== 'Invalid date' && one_of_billing_mode[3] !== ''">
          |
        </span>
      </span>
      <span v-else-if="isAccessNone"> &nbsp; </span>
      <span v-else>
        <br />
      </span>
      <span
        class="font-weight-regular pr-2"
        :class="$vuetify.display.smAndUp ? '' : 'pr-2'"
        v-if="one_of_billing_mode[3] !== 'Invalid date' && one_of_billing_mode[3] !== ''"
      >
        {{ one_of_billing_mode[2] }}
      </span>
      <span
        class="font-weight-bold"
        v-if="one_of_billing_mode[3] !== 'Invalid date' && one_of_billing_mode[3] !== ''"
      >
        {{ one_of_billing_mode[3] }}
      </span>
      <span v-if="showPayInProgress || showPayNowLinkThisCard" class="payment_in_process">
        <v-icon
          :class="$vuetify.display.smAndDown ? 'font-size--18' : 'font-size--24'"
          :icon="mdiCurrencyUsd"
        />
        {{ $t('L_payment_in_process') }}
      </span>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { openInNewWindow } from '@/mixins/openNewWindow';
import { currency, formatDate, toUTCDate } from '@/shared/util/formatters';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import type { IPolicyStorePolicy } from '@/modules/policy/store/policyStore.model';
import DynatraceClient from '@/plugins/dynatracePlugin';
import { useGlobalStore } from '@/stores/global/global.store';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { mdiAlert, mdiCurrencyUsd, mdiInformationOutline } from '@mdi/js';
import { BillingForm, BillingMode } from '@/shared/util/stringValues';
import { mapStores, mapState } from 'pinia';
import { RouteNames } from '@/router/routeNames';
import { DOCUMENT_OBJECT_TYPES_PREMIUM_DUE } from '@/shared/models/constants.models';
import { isBefore, isAfter, isSameDay, format } from 'date-fns';
import { Notifications, OnexAlert, OnexButton } from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'TermLifeCardPaymentAlert',
  mixins: [openInNewWindow],
  components: { OnexAlert, OnexButton },
  props: {
    indexPolicy: {
      type: Number,
      required: true,
      default: 0,
    },
  },
  data: () => ({
    paynowType: 'info',
    showMessageDialog: false,
    mdiCurrencyUsd,
    mdiAlert,
    mdiInformationOutline,
    Notifications,
  }),
  computed: {
    ...mapState(usePolicyStore, ['selectedPolicyIndex']),
    ...mapStores(useGlobalStore, usePolicyStore),
    icon() {
      if (this.type === 'warning') {
        return mdiAlert;
      } else if (this.type === 'info') {
        return mdiInformationOutline;
      }
    },
    isDirectMonthlyBill() {
      const billForm = this.selectedPolicy?.billingForm;
      const billMode = this.selectedPolicy?.billingMode || '';
      return billForm?.toLowerCase() === 'A1'.toLowerCase() && billMode === BillingMode['Mode-01'];
    },
    isPending() {
      return this.selectedPolicy?.pending;
    },
    isAccessNone() {
      return this.selectedPolicy?.data?.access === 'NONE';
    },
    selectedPolicy(): IPolicyStorePolicy {
      return this.policyStore.policies[this.indexPolicy];
    },
    showPayNowLinkThisCard() {
      let result = this.policyStore.showPayNowFeatureFlagAndPayNowLinkToggle;
      result =
        result &&
        !(
          !!this.selectedPolicy?.kubraPaymentStatus ||
          this.selectedPolicy?.paymentInfo?.payInProcess
        );
      result = result && this.selectedPolicy?.showPayNowLink;
      return result;
    },
    payNowLinkDesc() {
      if (
        this.indexPolicy === -1 &&
        this.policyStore.policies !== null &&
        this.policyStore.hasTermLife
      ) {
        return '';
      }
      let payNowLinkDesc = this.selectedPolicy?.payNowLinkDesc || '';
      const paidToDate = this.selectedPolicy?.paymentInfo?.paidToDate;
      const paidDateUTC = toUTCDate(paidToDate);
      const todayUTC = toUTCDate(new Date());
      const billForm = this.selectedPolicy?.billingForm;
      const DIRECT_BILL = billForm?.toLowerCase() === 'A1'.toLowerCase();
      const isPaidToDateBeforeToday = isBefore(paidDateUTC, todayUTC);
      const isPaidToDateAfterToday = isAfter(paidDateUTC, todayUTC);
      const isPaidToDateToday = isSameDay(paidDateUTC, todayUTC);

      const paidToDateFormatted = formatDate(paidToDate, this.globalStore.getDateFormat);
      const paymentAmt = this.selectedPolicy?.paynowInfo?.totalPaymentAmount || '0.00';

      this.paynowType = 'info';
      if (this.selectedPolicy?.paynowInfo?.inSufficientFunds) {
        this.paynowType = 'warning';
        payNowLinkDesc = this.$t('L_your_automatic_payment_of', { amount: paymentAmt });
      } else if (DIRECT_BILL) {
        if (paidToDate && isPaidToDateBeforeToday) {
          this.paynowType = 'warning';
          payNowLinkDesc = this.$t('L_a_payment_was_due_on', {
            amount: paymentAmt,
            date: paidToDateFormatted,
          });
        } else if (paidToDate && isPaidToDateAfterToday) {
          payNowLinkDesc = this.$t('L_a_payment_is_due_on', {
            amount: paymentAmt,
            date: paidToDateFormatted,
          });
        } else if (paidToDate && isPaidToDateToday) {
          payNowLinkDesc = this.$t('L_a_payment_is_due_today', { amount: paymentAmt });
        }
        if (this.selectedPolicy.paynowInfo?.hasMultipleOptions) {
          payNowLinkDesc += ` ${this.$t('L_a_payment_option_minimum', {
            amount: this.selectedPolicy.paynowInfo.minimumPaymentAmount,
          })}`;
        }
      } else {
        payNowLinkDesc = '';
      }
      return payNowLinkDesc;
    },
    payInProcess() {
      return this.selectedPolicy?.paymentInfo?.payInProcess;
    },
    kubraPaymentInProgress() {
      return (
        this.selectedPolicy?.kubraPaymentStatus !== null &&
        this.selectedPolicy?.kubraPaymentStatus !== undefined
      );
    },
    showPayInProgress() {
      return this.payInProcess || this.kubraPaymentInProgress;
    },
    one_of_billing_mode() {
      const billForm = this.selectedPolicy?.billingForm;
      const billMode = this.selectedPolicy?.billingMode || '';
      const paidToDate = this.selectedPolicy?.paymentInfo?.paidToDate || '';
      let modePremium = this.selectedPolicy?.modePremium;
      if (modePremium !== '' || modePremium !== null) {
        modePremium = currency(modePremium, 2);
      }
      const DIRECT_BILL = billForm?.toLowerCase() === 'A1'.toLowerCase();
      const TerminateAfterPaidOffDate = billForm?.toLowerCase() === 'H1'.toLowerCase();
      const policyPremiumDisplayLogicArray = [];
      if (DIRECT_BILL || TerminateAfterPaidOffDate) {
        if (billMode === BillingMode['Mode-01']) {
          policyPremiumDisplayLogicArray.push(this.$t('L_monthly_bill'));
        } else if (billMode === BillingMode['Mode-03']) {
          policyPremiumDisplayLogicArray.push(this.$t('quarterly_bill'));
        } else if (billMode === BillingMode['Mode-06']) {
          policyPremiumDisplayLogicArray.push(this.$t('L_semi_annual_bill'));
        } else if (billMode === BillingMode['Mode-12']) {
          policyPremiumDisplayLogicArray.push(this.$t('L_annual_bill'));
        } else {
          policyPremiumDisplayLogicArray.push(this.$t('L_monthly_bill'));
        }
        policyPremiumDisplayLogicArray.push(modePremium);
        if (TerminateAfterPaidOffDate) {
          policyPremiumDisplayLogicArray.push(this.$t('L_paid_through'));
        } else {
          policyPremiumDisplayLogicArray.push(this.$t('L_due_date'));
        }
        policyPremiumDisplayLogicArray.push(
          paidToDate && !isNaN(new Date(formatDate(paidToDate)).getTime())
            ? format(formatDate(paidToDate), 'MMM dd, yyyy')
            : ''
        );
      } else if (billForm?.toUpperCase() === BillingForm.B2) {
        // return monthly payment amount
        policyPremiumDisplayLogicArray.push(this.$t('L_monthly_bank_draft'));
        policyPremiumDisplayLogicArray.push(modePremium);
        policyPremiumDisplayLogicArray.push(this.$t('L_draft_date'));
        const draftDate = formatDate(this.selectedPolicy.nextDraftDate, 'MM/dd/yyyy');
        policyPremiumDisplayLogicArray.push(formatDate(draftDate, 'MMM dd, yyyy'));
      } else if (billForm?.toUpperCase() === BillingForm.C2) {
        // C2 = Government allotment
        policyPremiumDisplayLogicArray.push(this.$t('L_government_allotment'));
        policyPremiumDisplayLogicArray.push(modePremium);
        policyPremiumDisplayLogicArray.push(this.$t('L_due_date'));
        policyPremiumDisplayLogicArray.push(formatDate(paidToDate, 'MMM dd, yyyy'));
      } else if (billForm?.toUpperCase() === BillingForm.D2) {
        // D2 = payroll deduction
        policyPremiumDisplayLogicArray.push(this.$t('L_payroll_deduction'));
        policyPremiumDisplayLogicArray.push(modePremium);
        policyPremiumDisplayLogicArray.push(this.$t('L_due_date'));
        policyPremiumDisplayLogicArray.push(formatDate(paidToDate, 'MMM dd, yyyy'));
      } else {
        policyPremiumDisplayLogicArray.push('');
        policyPremiumDisplayLogicArray.push('');
        policyPremiumDisplayLogicArray.push('');
        policyPremiumDisplayLogicArray.push('');
      }
      return policyPremiumDisplayLogicArray;
    },
    currentBillPremiumDueNoticeDoc() {
      const currentBillDocs = this.globalStore.noticesAndConfirmations?.filter(
        (doc: { contract: string; metadata: { objectCode: string } }) =>
          doc.contract === this.selectedPolicy.policyNumber &&
          DOCUMENT_OBJECT_TYPES_PREMIUM_DUE.PREMIUM_DUE.includes(doc.metadata.objectCode)
      );
      currentBillDocs?.sort((a: { dateReceived: string }, b: { dateReceived: string }) => {
        const ad = new Date(a.dateReceived).getTime();
        const bd = new Date(b.dateReceived).getTime();
        return bd - ad;
      });
      return currentBillDocs?.[0];
    },
  },
  methods: {
    async payNow() {
      await this.policyStore.changePolicy({ selectedIndex: this.indexPolicy });
      // Calling payment call and if pay now exists then go through
      await this.policyStore.getPaymentInfo(this.policyStore.policies[this.indexPolicy].contractId);
      if (!this.showPayInProgress && this.showPayNowLinkThisCard) {
        // * Dynatrace Tag (https://primerica.atlassian.net/wiki/spaces/PD/pages/19955873/Payments+-+Custom+Dynatrace+Tags)
        try {
          const i = DynatraceClient.enterAction(
            `Dashboard_payNow_policy_${
              this.policyStore.policies[this.indexPolicy]?.contractId || this.indexPolicy
            }-${this.policyStore.policies[this.indexPolicy]?.insuredName}_Selected`
          );
          DynatraceClient.leaveAction(i);
        } catch (_error) {
          /* empty */
        }
        try {
          const i = DynatraceClient.enterAction('OneTimePayment_start');
          DynatraceClient.leaveAction(i);
        } catch (_error) {
          /* empty */
        }
        this.policyStore.paymentStep = 1;
        this.$router.push({
          name: RouteNames.PAYMENT_STEP,
          params: { contractId: this.selectedPolicyIndex },
        });
        // this.$router.push({ name: RouteNames.POLICY_DETAILS, params: { contractId: this.indexPolicy } });
        // await this.$router.push({
        //   name: RouteNames.GUIDANCE_CALCULATOR,
        //   params: { contractId: this.indexPolicy },
        // });
      } else {
        this.showMessageDialog = true;
      }
    },
    async getSingleDocumentUrl(requestObject: {
      contract: string;
      metadata: { objectCode: string };
    }) {
      const isWeb = CapacitorFacade.isWeb();
      if (isWeb) {
        await this.globalStore.getOnePolicyDocument(requestObject);
        return URL.createObjectURL(
          new Blob([this.globalStore.base64String], { type: 'application/pdf' })
        );
      } else {
        await this.globalStore.getOnePolicyDocument(requestObject);
        return this.globalStore.base64String;
      }
    },
    async openDocumentPDF() {
      const url = await this.getSingleDocumentUrl(this.currentBillPremiumDueNoticeDoc.metadata);
      const isWeb = CapacitorFacade.isWeb();
      await this.openInNewWindow({ url, inappbrowser: !isWeb });
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.payment_in_process {
  float: right;
}
.background_color_light_blue {
  background-color: #f5f7fa;
  padding-top: 10px;
  padding-bottom: 10px;
}
</style>
